import { useEffect, useState } from 'react';
import { WifiOff } from 'lucide-react';
import { useTranslation } from '../i18n';
import { useDiscreet } from '../security/discreet';

function readOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine !== false;
}

/** Tracks `navigator.onLine` through the window online/offline events. */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(readOnline);

  useEffect(() => {
    const update = () => setOnline(readOnline());
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    update();
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  return online;
}

/**
 * Slim banner shown while the device is offline. The shell loads from the
 * service worker; the game picks up again once the connection is back. In
 * discreet mode the text stays neutral (no mention of a game).
 */
export function OfflineNotice() {
  const { t } = useTranslation();
  const online = useOnlineStatus();
  const discreet = useDiscreet();

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 top-0 z-[55]"
    >
      {!online && (
        <div className="flex items-center justify-center gap-2 border-b border-white/10 bg-hc-overlay px-4 pb-1 pt-[max(0.25rem,env(safe-area-inset-top))] text-center text-xs text-white/80">
          <WifiOff className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            {t(discreet ? 'pwa.offline.bannerDiscreet' : 'pwa.offline.banner')}
          </span>
        </div>
      )}
    </div>
  );
}
